/* ── EXIT LOOP 3D LOGO VIEWER ── */
/* Loads Three.js + GLTFLoader from CDN, renders the rotating logo into #logo3d-canvas */

(function () {
  const CANVAS_ID = 'logo3d-canvas';

  function loadScript(src) {
    return new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = src;
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  async function initLogo3D() {
    const container = document.getElementById(CANVAS_ID);
    if (!container) return;

    const modelUrl = container.dataset.modelUrl;
    if (!modelUrl) return;

    // Load Three.js core + loaders from CDN (r128, matches theme constraints)
    // Load Three.js scripts in parallel
    await Promise.all([
      loadScript('https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js'),
    ]);
    await loadScript('https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/loaders/GLTFLoader.js');

    const THREE = window.THREE;

    const width = container.clientWidth;
    const height = container.clientHeight;

    const scene = new THREE.Scene();

    const camera = new THREE.PerspectiveCamera(40, width / height, 0.1, 1000);
    camera.position.set(0, 0, 5);

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    container.appendChild(renderer.domElement);

    // Lighting — lime rim light to match brand accent
    const ambient = new THREE.AmbientLight(0xffffff, 0.6);
    scene.add(ambient);

    const keyLight = new THREE.DirectionalLight(0xffffff, 1.1);
    keyLight.position.set(3, 4, 5);
    scene.add(keyLight);

    const rimLight = new THREE.DirectionalLight(0xC8FF00, 1.4);
    rimLight.position.set(-4, 2, -3);
    scene.add(rimLight);

    let model = null;

    const loader = new THREE.GLTFLoader();
    loader.load(
      modelUrl,
      (gltf) => {
        model = gltf.scene;

        // Original material — bright white with lime emissive
        model.traverse((child) => {
          if (child.isMesh) {
            child.material = new THREE.MeshStandardMaterial({
              color: 0xF0EDE6,
              emissive: 0xC8FF00,
              emissiveIntensity: 0.18,
              metalness: 0.3,
              roughness: 0.35,
            });
          }
        });

        // Auto-center and scale to fit viewport
        const box = new THREE.Box3().setFromObject(model);
        const size = box.getSize(new THREE.Vector3());
        const center = box.getCenter(new THREE.Vector3());

        model.position.x -= center.x;
        model.position.y -= center.y;
        model.position.z -= center.z;

        const maxDim = Math.max(size.x, size.y, size.z);
        const scale = 2.4 / maxDim;
        model.scale.setScalar(scale);

        scene.add(model);



        container.classList.add('is-loaded');
      },
      undefined,
      (err) => {
        console.error('Logo3D load error:', err);
        container.classList.add('has-error');
      }
    );

    // ── DRAG TO ROTATE (mouse + touch) ──
    let isDragging = false;
    let prevX = 0;
    let prevY = 0;
    let velocityY = 0;
    let velocityX = 0;
    let rotY = 0;
    let rotX = 0;
    let autoSpin = true;

    function pointerDown(x, y) {
      isDragging = true;
      autoSpin = false;
      prevX = x;
      prevY = y;
      velocityY = 0;
      velocityX = 0;
      container.classList.add('is-dragging');
    }

    function pointerMove(x, y) {
      if (!isDragging) return;
      const dx = x - prevX;
      const dy = y - prevY;
      velocityY = dx * 0.005;
      velocityX = dy * 0.005;
      rotY += velocityY;
      rotX += velocityX;
      prevX = x;
      prevY = y;
    }

    function pointerUp() {
      isDragging = false;
      container.classList.remove('is-dragging');
    }

    // Mouse events
    container.addEventListener('mousedown', (e) => {
      pointerDown(e.clientX, e.clientY);
    });
    window.addEventListener('mousemove', (e) => {
      pointerMove(e.clientX, e.clientY);
    });
    window.addEventListener('mouseup', pointerUp);

    // Touch events
    container.addEventListener('touchstart', (e) => {
      const t = e.touches[0];
      pointerDown(t.clientX, t.clientY);
    }, { passive: true });
    window.addEventListener('touchmove', (e) => {
      if (!isDragging) return;
      const t = e.touches[0];
      pointerMove(t.clientX, t.clientY);
    }, { passive: true });
    window.addEventListener('touchend', pointerUp);

    function animate() {
      requestAnimationFrame(animate);
      if (model) {
        if (isDragging) {
          model.rotation.y = rotY;
          model.rotation.x = Math.max(-0.6, Math.min(0.6, rotX));
        } else {
          // Momentum decay after release
          if (Math.abs(velocityY) > 0.0001 || Math.abs(velocityX) > 0.0001) {
            velocityY *= 0.95;
            velocityX *= 0.95;
            rotY += velocityY;
            rotX += velocityX;
            model.rotation.y = rotY;
            model.rotation.x = Math.max(-0.6, Math.min(0.6, rotX));
          } else if (autoSpin) {
            rotY += 0.006;
            model.rotation.y = rotY;
            model.rotation.x += (0 - model.rotation.x) * 0.02;
          } else {
            // Resume gentle auto-spin from current position after momentum settles
            autoSpin = true;
          }
        }
      }
      renderer.render(scene, camera);
    }
    animate();

    // Resize handling
    window.addEventListener('resize', () => {
      const w = container.clientWidth;
      const h = container.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initLogo3D);
  } else {
    initLogo3D();
  }
})();
